import { z } from 'zod';

/**
 * Firestore Timestamp shape, agnostic to client vs admin SDK.
 * Both expose `toMillis()` and `toDate()`; we model only what we read/write.
 *
 * The methods are optional because seed/prerender/test fixtures often carry a
 * bare `{ seconds, nanoseconds }` shape with no prototype methods.
 */
export interface Timestamp {
  toMillis?: () => number;
  toDate?: () => Date;
  seconds: number;
  nanoseconds: number;
}

/**
 * Validates the Timestamp shape but returns the value **by reference** — no
 * structural clone (ADR-0028). This is the definitive fix for the "read-echo
 * class" (ADR-0027 "Known remaining breakage"): the previous `z.object({...})`
 * form ran `.parse()`, which copies a real Firestore `Timestamp`'s
 * `toMillis`/`toDate` methods onto a brand-new plain object that is no longer
 * `instanceof Timestamp`. Reads (`{ ...parseOrThrow(snap.data()), id }`) then
 * handed callers pseudo-Timestamps, and echoing one back into `set()` failed
 * Firestore encoding. A `z.custom` predicate validates in place and passes the
 * original through untouched, so reads now return real `Timestamp` instances
 * that round-trip through `set()` unchanged.
 *
 * The predicate reads the numeric VALUE (`Number.isFinite(v.seconds)`) rather
 * than testing property presence with `Object.hasOwn(v, 'seconds')`: a real
 * `firebase-admin/firestore` Timestamp exposes `seconds`/`nanoseconds` as
 * prototype GETTERS (its own enumerable props are `_seconds`/`_nanoseconds`),
 * so an own-property check would wrongly reject admin Timestamps. Reading the
 * getter value works for both client and admin SDKs.
 *
 * `Number.isFinite` also rejects `NaN`, `Infinity`, and non-numbers with no
 * coercion — a real Firestore `Timestamp` (proto int64 seconds / int32 nanos)
 * can never be non-finite, and a raw JS `Date` has no numeric `seconds`, so the
 * do-not-do rule-4 backstop ("a `Date` must not validate as a Timestamp")
 * holds. This is strictly tighter than the previous `z.object` form, which
 * accepted `Infinity`.
 */
export const TimestampSchema = z.custom<Timestamp>(
  (v): v is Timestamp =>
    typeof v === 'object' &&
    v !== null &&
    Number.isFinite((v as Timestamp).seconds) &&
    Number.isFinite((v as Timestamp).nanoseconds),
  { message: 'Expected a Firestore Timestamp ({ seconds, nanoseconds })' },
);

/** ISO-4217 currency code, MVP scoped to MYR. */
export const CurrencySchema = z.enum(['MYR', 'IDR', 'PHP']).describe('ISO-4217 currency');
export type Currency = z.infer<typeof CurrencySchema>;

/** Money is stored as integer minor units (sen for MYR). */
export const MoneySchema = z
  .number()
  .int()
  .nonnegative()
  .describe('Integer minor units (e.g., sen for MYR). 100 = RM 1.00');
export type Money = z.infer<typeof MoneySchema>;

/** A short slug used in URLs (tenant subdomain, outlet slug). */
export const SlugSchema = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, digits, single dashes')
  .min(3)
  .max(40)
  .describe('URL-safe slug');
export type Slug = z.infer<typeof SlugSchema>;

/** A short canonical ID (nanoid-style, 10 chars by default). */
export const ShortIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]+$/)
  .min(6)
  .max(24)
  .describe('Short URL-safe ID');
export type ShortId = z.infer<typeof ShortIdSchema>;

/** A Firebase Auth user ID. */
export const UidSchema = z.string().min(1).max(128).describe('Firebase Auth uid');
export type Uid = z.infer<typeof UidSchema>;

/**
 * Locale codes Kilat supports.
 * - `en`: English (default)
 * - `ms`: Bahasa Malaysia
 * - `zh`: Simplified Chinese (zh-Hans) — Malaysian Chinese community
 * - `ta`: Tamil — Malaysian Indian community (most-spoken Indian language in MY)
 * SEA expansion locales (`id`, `tl`, etc.) added later.
 */
export const LocaleSchema = z.enum(['en', 'ms', 'zh', 'ta']).describe('Supported locale');
export type Locale = z.infer<typeof LocaleSchema>;
export const DEFAULT_LOCALE: Locale = 'en';
export const ALL_LOCALES: readonly Locale[] = ['en', 'ms', 'zh', 'ta'] as const;

/** A hex colour string (e.g., #0d7a4f). */
export const HexColourSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, '6-digit hex with leading #')
  .describe('Hex colour like #0d7a4f');
export type HexColour = z.infer<typeof HexColourSchema>;

/** WGS84 geo coordinates. */
export const GeoSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type Geo = z.infer<typeof GeoSchema>;

/** i18n field — keyed by locale; en is required, others optional. */
export const I18nStringSchema = z
  .object({ en: z.string() })
  .catchall(z.string())
  .describe('Map of locale to string; en required');
export type I18nString = z.infer<typeof I18nStringSchema>;
